import type { PrintoutSettings } from "./Header/usePrintout";
import {
	applyPresetSettings,
	PRESET_SETTING_KEYS,
	type PresetSettings,
	type PrintoutPreset,
	type PrintoutPresetsResponse,
	toPresetSettings,
} from "./printoutPresets";

/**
 * How long the printout settings panel waits after the last change before saving it. Printout 1 and
 * the saved printouts share it, so every printout saves on the same rhythm.
 */
export const PRINTOUT_AUTOSAVE_DELAY_MS = 5_000;

/** A saved printout's settings waiting to be written — bound to the printout they were edited on. */
export type PendingPresetSave = {
	/** The department the edit was made in: a save still waiting at a department switch is sent there. */
	departmentId: string;
	presetId: string;
	/** For the failure message only. */
	presetName: string;
	settings: PresetSettings;
};

export type PresetAutosave = {
	/** Replaces what's waiting and restarts the delay. A wait for a different printout is saved first. */
	schedule: (next: PendingPresetSave) => void;
	/**
	 * Saves what's waiting right now, to the printout it was edited on, and resolves once every save
	 * still on its way (sent by the timer or an earlier flush) has answered too — so a removal can wait
	 * for them. Resolves `true` when all of those saves worked, `false` when any failed: a step that
	 * copies the saved printouts (Save as default) must not go ahead over a save that didn't land.
	 */
	flush: () => Promise<boolean>;
};

/**
 * The saved printouts' debounced autosave. What waits carries its own printout id, so switching
 * printouts, downloading or closing the window saves an edit to the printout it was made on — never
 * to whichever printout happens to be selected when the delay runs out.
 *
 * `save` shows and tracks its own failures and resolves whether the save worked — it never rejects — so
 * a failure is reported to the user once, by the save, and only counted here.
 */
export const createPresetAutosave = (save: (pending: PendingPresetSave) => Promise<boolean>, delayMs: number): PresetAutosave => {
	let pending: PendingPresetSave | null = null;
	let timer: ReturnType<typeof setTimeout> | null = null;
	/** Saves sent and not answered yet. Each leaves the set when it answers. */
	const inFlight = new Set<Promise<boolean>>();

	const clearTimer = (): void => {
		if (timer === null) return;
		clearTimeout(timer);
		timer = null;
	};

	const send = (toSave: PendingPresetSave): void => {
		const saving = save(toSave);
		inFlight.add(saving);
		void saving.then(() => inFlight.delete(saving));
	};

	const flush = async (): Promise<boolean> => {
		clearTimer();
		const toSave = pending;
		pending = null;
		if (toSave) send(toSave);
		const results = await Promise.all([...inFlight]);
		return results.every((saved) => saved);
	};

	return {
		schedule: (next) => {
			if (pending && pending.presetId !== next.presetId) void flush();
			clearTimer();
			pending = next;
			timer = setTimeout(() => void flush(), delayMs);
		},
		flush,
	};
};

/**
 * The saved printout in use, or `null` for Printout 1. An id the list no longer has (removed in another
 * window) is Printout 1 too — the same thing the server does when the active printout is removed.
 */
export const resolveActivePreset = (state: PrintoutPresetsResponse | undefined): PrintoutPreset | null => {
	if (!state || state.activePresetId === null) return null;
	// DEFAULT: an active id missing from the list means the preset is gone; Printout 1 is what's left.
	return state.presets.find((preset) => preset.id === state.activePresetId) ?? null;
};

/** Whether a setting belongs to a saved printout. The rest (Summary-only) always stay Printout 1's. */
export const isPresetSettingKey = (key: keyof PrintoutSettings): key is keyof PresetSettings => Object.hasOwn(PRESET_SETTING_KEYS, key);

/**
 * The settings a saved printout opens with: this session's latest edits when there are any (a save of
 * them may still be on its way, so the cached copy can be older), otherwise the server's copy.
 */
export const seedPresetSettings = (preset: PrintoutPreset, sessionEdits: ReadonlyMap<string, PresetSettings>): PresetSettings =>
	// DEFAULT: no edits this session means the server's copy is the latest.
	sessionEdits.get(preset.id) ?? preset.settings;

/** A saved printout's settings with one setting changed, through the same conversion the API uses. */
export const withPresetSetting = <K extends keyof PrintoutSettings>(
	printout1Settings: PrintoutSettings,
	presetSettings: PresetSettings,
	key: K,
	value: PrintoutSettings[K],
): PresetSettings => toPresetSettings({ ...applyPresetSettings(printout1Settings, presetSettings), [key]: value });
